import * as React from 'react';
import * as MenuPrimitive from '@radix-ui/react-dropdown-menu';
import { Check, ChevronRight, Circle } from 'lucide-react';
import { cn } from '@relay-towing/tokens';
import {
  menuContentRecipe,
  menuIndicatorRecipe,
  menuItemRecipe,
  menuLabelRecipe,
  menuSeparatorRecipe,
  menuShortcutRecipe,
  type MenuRecipeProps,
} from '../recipes/menu.recipe';

/**
 * The menu behind every "…" row-action button.
 *
 * Radix handles the parts that are painful by hand: typeahead, arrow-key
 * navigation, focus return to the trigger on close, and collision-aware
 * placement so a menu on the last table row opens upwards instead of off the
 * bottom of the page.
 *
 *   <DropdownMenu>
 *     <DropdownMenuTrigger asChild>
 *       <Button variant="ghost" size="icon-sm" aria-label="Actions">…</Button>
 *     </DropdownMenuTrigger>
 *     <DropdownMenuContent>
 *       <DropdownMenuItem onSelect={edit}>Edit</DropdownMenuItem>
 *       <DropdownMenuSeparator />
 *       <DropdownMenuItem tone="destructive" onSelect={remove}>Delete</DropdownMenuItem>
 *     </DropdownMenuContent>
 *   </DropdownMenu>
 */
export const DropdownMenu = MenuPrimitive.Root;
export const DropdownMenuTrigger = MenuPrimitive.Trigger;

export interface DropdownMenuContentProps
  extends React.ComponentPropsWithoutRef<typeof MenuPrimitive.Content>,
    MenuRecipeProps {}

export const DropdownMenuContent = React.forwardRef<
  React.ElementRef<typeof MenuPrimitive.Content>,
  DropdownMenuContentProps
>(function DropdownMenuContent(
  { className, size, sideOffset = 4, align = 'end', ...props },
  ref
) {
  return (
    <MenuPrimitive.Portal>
      <MenuPrimitive.Content
        ref={ref}
        sideOffset={sideOffset}
        align={align}
        className={cn(menuContentRecipe({ size }), className)}
        {...props}
      />
    </MenuPrimitive.Portal>
  );
});

export interface DropdownMenuItemProps
  extends React.ComponentPropsWithoutRef<typeof MenuPrimitive.Item> {
  /** Use `destructive` for deletes and anything irreversible. */
  tone?: 'default' | 'destructive';
  inset?: boolean;
}

export const DropdownMenuItem = React.forwardRef<
  React.ElementRef<typeof MenuPrimitive.Item>,
  DropdownMenuItemProps
>(function DropdownMenuItem({ className, tone, inset, ...props }, ref) {
  return (
    <MenuPrimitive.Item
      ref={ref}
      className={cn(menuItemRecipe({ tone, inset }), className)}
      {...props}
    />
  );
});

export const DropdownMenuCheckboxItem = React.forwardRef<
  React.ElementRef<typeof MenuPrimitive.CheckboxItem>,
  React.ComponentPropsWithoutRef<typeof MenuPrimitive.CheckboxItem>
>(function DropdownMenuCheckboxItem({ className, children, ...props }, ref) {
  return (
    <MenuPrimitive.CheckboxItem
      ref={ref}
      className={cn(menuItemRecipe({ inset: true }), className)}
      {...props}
    >
      <span className={menuIndicatorRecipe()}>
        <MenuPrimitive.ItemIndicator>
          <Check className="size-4" />
        </MenuPrimitive.ItemIndicator>
      </span>
      {children}
    </MenuPrimitive.CheckboxItem>
  );
});

export const DropdownMenuRadioGroup = MenuPrimitive.RadioGroup;

/**
 * One of several, where exactly one is on — how a list is sorted, grouped or
 * filtered.
 *
 * The checkbox twin above says "this is on, and so might the next one be". A
 * radio item says picking this one puts the last one back, which is the
 * difference between a filter you stack and a mode you switch.
 */
export const DropdownMenuRadioItem = React.forwardRef<
  React.ElementRef<typeof MenuPrimitive.RadioItem>,
  React.ComponentPropsWithoutRef<typeof MenuPrimitive.RadioItem> & {
    /**
     * Off drops the radio mark and its gutter, for a menu whose items show
     * which one is on by themselves (a list of status badges, say), so they
     * sit flush left.
     */
    marked?: boolean;
  }
>(function DropdownMenuRadioItem(
  { className, children, marked = true, ...props },
  ref
) {
  return (
    <MenuPrimitive.RadioItem
      ref={ref}
      className={cn(menuItemRecipe({ inset: marked }), className)}
      {...props}
    >
      {marked ? (
        <span className={menuIndicatorRecipe()}>
          <MenuPrimitive.ItemIndicator>
            <Circle className="size-2 fill-current" />
          </MenuPrimitive.ItemIndicator>
        </span>
      ) : null}
      {children}
    </MenuPrimitive.RadioItem>
  );
});

export const DropdownMenuLabel = React.forwardRef<
  React.ElementRef<typeof MenuPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof MenuPrimitive.Label>
>(function DropdownMenuLabel({ className, ...props }, ref) {
  return (
    <MenuPrimitive.Label
      ref={ref}
      className={cn(menuLabelRecipe(), className)}
      {...props}
    />
  );
});

export const DropdownMenuSeparator = React.forwardRef<
  React.ElementRef<typeof MenuPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof MenuPrimitive.Separator>
>(function DropdownMenuSeparator({ className, ...props }, ref) {
  return (
    <MenuPrimitive.Separator
      ref={ref}
      className={cn(menuSeparatorRecipe(), className)}
      {...props}
    />
  );
});
